import { type InputHTMLAttributes, forwardRef, useId } from "react";
import {
	type FieldSize,
	fieldClasses,
	fieldErrorClass,
	fieldHelperClass,
	fieldLabelClass,
} from "./field.ts";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
	label?: string;
	error?: string;
	helperText?: string;
	size?: FieldSize;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
	({ label, error, helperText, size = "md", className = "", id: idProp, ...props }, ref) => {
		const generatedId = useId();
		const id = idProp ?? generatedId;
		const errorId = `${id}-error`;
		const helperId = `${id}-helper`;

		return (
			<div className="flex flex-col gap-1.5">
				{label && (
					<label htmlFor={id} className={fieldLabelClass}>
						{label}
					</label>
				)}
				<input
					ref={ref}
					id={id}
					aria-invalid={error ? "true" : undefined}
					aria-describedby={
						[error ? errorId : null, helperText ? helperId : null].filter(Boolean).join(" ") ||
						undefined
					}
					className={fieldClasses(size, !!error, className)}
					{...props}
				/>
				{error && (
					<p id={errorId} role="alert" className={fieldErrorClass}>
						{error}
					</p>
				)}
				{helperText && !error && (
					<p id={helperId} className={fieldHelperClass}>
						{helperText}
					</p>
				)}
			</div>
		);
	},
);

Input.displayName = "Input";
